import React from "react";
import { Link } from "react-router-dom";
import HeroImg from "../../assets/images/announcement_2026-02-22/DeepTrading_Logo_For_Dark.png";
import StrategiesImg from "../../assets/images/announcement_2026-02-22/01_trading_strategies_backtesting.jpg";
import MarketImg from "../../assets/images/announcement_2026-02-22/02_market_analysis_general_ticker.jpg";
import BrokerImg from "../../assets/images/announcement_2026-02-22/03_broker_portfolio_holdings_small.jpg";
import "./press.css";

const PressRelease = () => (
  <div className="bodySec pressSec">
    <div className="container">
      <div className="pressBack">
        <Link to="/press">Back to Press</Link>
      </div>

      <div className="pressArticlePanel">
        <Link to="/press" className="pressCloseBtn" aria-label="Close and return to press list">
          x
        </Link>

        <img src={HeroImg} alt="DeepTrading.ai Release" className="pressHeroImage" />

        <p className="pressEyebrow"><strong>FOR IMMEDIATE RELEASE</strong></p>

        <h1 className="pressTitle">
          DeepTrading.ai Announces Major Product Release Introducing Trading Strategies, Backtesting,
          Market Analysis, Broker Portfolio Connections, and REST API
        </h1>

        <p className="pressDek">
          Most requested upgrades make DeepTrading.ai more actionable while keeping the experience simple,
          visual, and transparent
        </p>

        <p>
          <strong>BAYONNE, NJ (March 3, 2026)</strong> — DeepTrading.ai today announced a major product update
          that expands the platform from "predictions and charts" into a more complete, action-oriented
          workflow for retail investors and traders. The release introduces rule-based Trading Strategies,
          built-in Backtesting with Buy &amp; Hold comparisons, AI-powered Market Analysis briefs, read-only
          broker portfolio connections, faster scanning via new predicted Gain/Loss columns, CSV export,
          and a REST API add-on for programmatic access.
        </p>

        <p>
          "This is one of our biggest releases ever," said Nektarios Kalogridis, Founder and CEO of
          DeepTrading.ai. "Customers have consistently asked for ways to turn predictions into a clear
          workflow without making the platform complicated. This release delivers that while staying
          transparent and easy to understand."
        </p>

        <h2>New features include</h2>
        <ul>
          <li>- <strong>Trading Strategies (rule-based):</strong> Build, save, and run strategies on top of DeepTrading predictions, with simple controls designed for everyday investors.</li>
          <li>- <strong>Backtesting:</strong> Simulated trades and portfolio equity growth over time, with a detailed trades table and summary metrics.</li>
          <li>- <strong>Buy &amp; Hold Return comparison:</strong> A side-by-side baseline so results can be judged instantly against passive holding.</li>
          <li>- <strong>Market Analysis:</strong> A General Market Brief that loads immediately, plus per-ticker briefs with Subject + Sentiment after market close.</li>
          <li>- <strong>Broker Portfolio (read-only):</strong> Connect brokerage accounts and overlay DeepTrading predictions directly on holdings.</li>
          <li>- <strong>Predicted Gain/Loss columns:</strong> Scan implied upside/downside by horizon in Data Search for faster opportunity review.</li>
          <li>- <strong>CSV export:</strong> Export Backtesting trades and the current filtered/sorted Data Search view to CSV.</li>
          <li>- <strong>Model Improvements (Model 100):</strong> Stronger training checks and more consistent cross-horizon stability.</li>
          <li>- <strong>Accuracy improvements:</strong> Hybrid Accuracy now appears more consistently across the dashboard.</li>
          <li>- <strong>Usability and visual polish:</strong> Cleaner dashboard readability, clearer chart thumbnails, improved table controls, and resizable panels.</li>
          <li>- <strong>REST API access (add-on):</strong> Secure per-user API keys with credit-based usage caps, supporting predictions, movers, briefs, and more.</li>
        </ul>

        <h2>Trading Strategies and Backtesting designed for practical decision-making</h2>
        <p>
          The new Trading Strategies feature allows users to define simple rules based on predicted direction
          and magnitude, save strategies, and generate backtests to answer a practical question:
          "If I followed these signals historically, what would have happened?"
        </p>
        <p>Each backtest produces:</p>
        <ul>
          <li>A summary view (Trades, Win Rate, Total Return, Final Equity)</li>
          <li>A detailed trades table showing each simulated decision and running equity</li>
          <li>Buy &amp; Hold Return for the same period so users can compare strategy results to a passive baseline</li>
        </ul>
        <p>
          Backtests are computed on business days and are for educational purposes. DeepTrading.ai does not
          place trades.
        </p>
        <img src={StrategiesImg} alt="Trading Strategies and Backtesting" className="pressInlineImage" />

        <h2>Market Analysis briefs that are short, readable, and useful</h2>
        <p>
          DeepTrading.ai now provides daily, AI-powered market briefs for each tracked ticker after market close.
          Each brief is intentionally concise and includes:
        </p>
        <ul>
          <li>A fast-loading General Market Brief</li>
          <li>A one-line Subject for quick scanning</li>
          <li>A simple Sentiment label (Positive / Neutral / Negative)</li>
          <li>A short "what happened + what's next" style summary</li>
        </ul>
        <img src={MarketImg} alt="Market Analysis" className="pressInlineImage" />

        <h2>Read-only broker portfolio connections with predictions overlaid on holdings</h2>
        <p>
          Users can connect brokerage accounts in read-only mode and view holdings directly inside DeepTrading.ai.
          Supported holdings can display relevant predictions and context alongside common portfolio fields.
          If a symbol is not yet supported, it can still be shown and flagged via Request Support.
        </p>
        <img src={BrokerImg} alt="Broker Portfolio" className="pressInlineImage" />

        <h2>Faster scanning with predicted Gain/Loss and CSV export</h2>
        <p>
          Data Search now includes predicted Gain/Loss-style columns by horizon, making it easier to sort and
          filter across the universe to review the biggest predicted movers. CSV export is now available for:
        </p>
        <ul>
          <li>Backtesting trades tables</li>
          <li>The current filtered/sorted Data Search view</li>
        </ul>

        <h2>REST API access for power users</h2>
        <p>
          DeepTrading.ai is also rolling out a REST API add-on for programmatic access using secure per-user API keys
          and credit-based usage caps. Available endpoints include stock lists, top movers, per-ticker predictions by
          horizon, gain/loss values, ticker brief sentiment/subject, and the general market brief.
        </p>

        <h2>Availability and plan notes</h2>
        <p>
          These features are available now in the DeepTrading.ai dashboard. DeepTrading.ai offers a 7-day free trial
          and includes two subscription tiers:
        </p>
        <ul>
          <li><strong>Basic:</strong> Daily / Two-Day / Weekly horizons</li>
          <li><strong>Advanced:</strong> all horizons from 1D to 1Y</li>
        </ul>

        <h2>Important note</h2>
        <p>
          DeepTrading.ai provides educational tools and research support. It does not place trades, and content is not
          financial advice.
        </p>

        <h2>About DeepTrading.ai</h2>
        <p>
          DeepTrading.ai is an AI-powered stock prediction and market research platform designed for retail investors
          and traders. It provides multi-horizon predictions, visual track record context, and a workflow that helps
          users build strategies, run backtests, and review market briefs with clarity and transparency.
        </p>

        <h2>Media Contact</h2>
        <p>
          Nektarios Kalogridis
          <br />
          Founder and CEO, DeepTrading.ai
          <br />
          Email: <a href="mailto:mail@nektarios.com">mail@nektarios.com</a>
        </p>
        <p>
          Website: <a href="https://www.deeptrading.ai">https://www.deeptrading.ai</a>
        </p>
      </div>
    </div>
  </div>
);

export default PressRelease;
